import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  AlertCircle,
  Search,
  CheckCircle,
  Briefcase,
  Loader2,
  X,
  Shield,
  Delete,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Plus,
  Pencil
} from 'lucide-react';
import toast from 'react-hot-toast';
import { receptoresApi } from '../../utils/api';
import ExoneracionModal from '../../components/ExoneracionModal';
import { getDistritosOptions } from '../../utils/costaRicaLocations';

const TIPOS_IDENTIFICACION = [
  { value: '01', label: 'Cédula Física', maxLength: 9, mask: '0-0000-0000', placeholder: '0-0000-0000' },
  { value: '02', label: 'Cédula Jurídica', maxLength: 10, mask: '0-000-000000', placeholder: '3-101-XXXXXX' },
  { value: '03', label: 'DIMEX', maxLength: 12, mask: '000000000000', placeholder: '000000000000' },
  { value: '04', label: 'NITE', maxLength: 10, mask: '0000000000', placeholder: '0000000000' },
  { value: '05', label: 'Extranjero', maxLength: 20, mask: null, placeholder: 'Identificación' },
];

// Los tipos de exoneración e instituciones se cargan desde la API

// Format identification based on type
const formatIdentificacion = (value, tipo) => {
  const digits = value.replace(/\D/g, '');

  switch (tipo) {
    case '01': // Cédula Física: 0-0000-0000
      if (digits.length <= 1) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 1)}-${digits.slice(1)}`;
      return `${digits.slice(0, 1)}-${digits.slice(1, 5)}-${digits.slice(5, 9)}`;

    case '02': // Cédula Jurídica: 0-000-000000
      if (digits.length <= 1) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 1)}-${digits.slice(1)}`;
      return `${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4, 10)}`;

    default:
      return digits;
  }
};

// Get raw digits from formatted value
const getDigits = (value) => value.replace(/\D/g, '');

// Costa Rica provinces
const PROVINCIAS = [
  { value: '1', label: 'San José' },
  { value: '2', label: 'Alajuela' },
  { value: '3', label: 'Cartago' },
  { value: '4', label: 'Heredia' },
  { value: '5', label: 'Guanacaste' },
  { value: '6', label: 'Puntarenas' },
  { value: '7', label: 'Limón' },
];

// Cantones by province (simplified - just a few for demo)
const CANTONES = {
  '1': [
    { value: '01', label: 'San José' },
    { value: '02', label: 'Escazú' },
    { value: '03', label: 'Desamparados' },
    { value: '04', label: 'Puriscal' },
    { value: '05', label: 'Tarrazú' },
    { value: '06', label: 'Aserrí' },
    { value: '07', label: 'Mora' },
    { value: '08', label: 'Goicoechea' },
    { value: '09', label: 'Santa Ana' },
    { value: '10', label: 'Alajuelita' },
    { value: '11', label: 'Vázquez de Coronado' },
    { value: '12', label: 'Acosta' },
    { value: '13', label: 'Tibás' },
    { value: '14', label: 'Moravia' },
    { value: '15', label: 'Montes de Oca' },
    { value: '16', label: 'Turrubares' },
    { value: '17', label: 'Dota' },
    { value: '18', label: 'Curridabat' },
    { value: '19', label: 'Pérez Zeledón' },
    { value: '20', label: 'León Cortés' },
  ],
  '2': [
    { value: '01', label: 'Alajuela' },
    { value: '02', label: 'San Ramón' },
    { value: '03', label: 'Grecia' },
    { value: '04', label: 'San Mateo' },
    { value: '05', label: 'Atenas' },
    { value: '06', label: 'Naranjo' },
    { value: '07', label: 'Palmares' },
    { value: '08', label: 'Poás' },
    { value: '09', label: 'Orotina' },
    { value: '10', label: 'San Carlos' },
    { value: '11', label: 'Zarcero' },
    { value: '12', label: 'Sarchí' },
    { value: '13', label: 'Upala' },
    { value: '14', label: 'Los Chiles' },
    { value: '15', label: 'Guatuso' },
    { value: '16', label: 'Río Cuarto' },
  ],
  '3': [
    { value: '01', label: 'Cartago' },
    { value: '02', label: 'Paraíso' },
    { value: '03', label: 'La Unión' },
    { value: '04', label: 'Jiménez' },
    { value: '05', label: 'Turrialba' },
    { value: '06', label: 'Alvarado' },
    { value: '07', label: 'Oreamuno' },
    { value: '08', label: 'El Guarco' },
  ],
  '4': [
    { value: '01', label: 'Heredia' },
    { value: '02', label: 'Barva' },
    { value: '03', label: 'Santo Domingo' },
    { value: '04', label: 'Santa Bárbara' },
    { value: '05', label: 'San Rafael' },
    { value: '06', label: 'San Isidro' },
    { value: '07', label: 'Belén' },
    { value: '08', label: 'Flores' },
    { value: '09', label: 'San Pablo' },
    { value: '10', label: 'Sarapiquí' },
  ],
  '5': [
    { value: '01', label: 'Liberia' },
    { value: '02', label: 'Nicoya' },
    { value: '03', label: 'Santa Cruz' },
    { value: '04', label: 'Bagaces' },
    { value: '05', label: 'Carrillo' },
    { value: '06', label: 'Cañas' },
    { value: '07', label: 'Abangares' },
    { value: '08', label: 'Tilarán' },
    { value: '09', label: 'Nandayure' },
    { value: '10', label: 'La Cruz' },
    { value: '11', label: 'Hojancha' },
  ],
  '6': [
    { value: '01', label: 'Puntarenas' },
    { value: '02', label: 'Esparza' },
    { value: '03', label: 'Buenos Aires' },
    { value: '04', label: 'Montes de Oro' },
    { value: '05', label: 'Osa' },
    { value: '06', label: 'Quepos' },
    { value: '07', label: 'Golfito' },
    { value: '08', label: 'Coto Brus' },
    { value: '09', label: 'Parrita' },
    { value: '10', label: 'Corredores' },
    { value: '11', label: 'Garabito' },
  ],
  '7': [
    { value: '01', label: 'Limón' },
    { value: '02', label: 'Pococí' },
    { value: '03', label: 'Siquirres' },
    { value: '04', label: 'Talamanca' },
    { value: '05', label: 'Matina' },
    { value: '06', label: 'Guácimo' },
  ],
};

export default function FormularioCliente({ isModal = false, onSave = null, onCancel = null }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !isModal && !!id;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [errors, setErrors] = useState({});
  const [consultandoHacienda, setConsultandoHacienda] = useState(false);
  const [actividadesHacienda, setActividadesHacienda] = useState([]);
  const [haciendaConsultado, setHaciendaConsultado] = useState(false);
  const [userChangedIdentificacion, setUserChangedIdentificacion] = useState(false);

  // Estados para exoneración
  const [showExoneracionModal, setShowExoneracionModal] = useState(false);
  const [exoneraciones, setExoneraciones] = useState([]);
  const [loadingExoneraciones, setLoadingExoneraciones] = useState(false);
  const [editingExoneracion, setEditingExoneracion] = useState(null);
  const [mostrarCabys, setMostrarCabys] = useState(false);
  const [numeroExoneracionInput, setNumeroExoneracionInput] = useState('');
  const [tipoExoneracionSeleccionado, setTipoExoneracionSeleccionado] = useState('');
  const [modoExoneracion, setModoExoneracion] = useState('hacienda');

  const [cliente, setCliente] = useState({
    tipoIdentificacion: '02',
    identificacion: '',
    nombre: '',
    nombreComercial: '',
    provincia: '',
    canton: '',
    distrito: '',
    otrasSenas: '',
    telefonoCodigo: '506',
    telefonoNumero: '',
    email: '',
    emailFacturacion: '',
    actividadEconomica: '',
    actividadEconomicaDescripcion: '',
    // Configuración de facturación
    generaFacturaElectronica: true, // true=FE, false=Tiquete
    monedaDefecto: 'CRC',
    condicionVentaDefecto: '01', // 01=Contado, 02=Crédito
    diasCredito: 0,
    // Configuración de crédito
    tieneCuentaCredito: false,
    limiteCredito: null,
    creditoSuspendido: false,
    notasCredito: '',
    // Campos de exoneración
    exoneracionNumero: '',
    exoneracionTipoDoc: '',
    exoneracionTipoDocDesc: '',
    exoneracionInstitucionCodigo: '',
    exoneracionInstitucionNombre: '',
    exoneracionFechaEmision: null,
    exoneracionFechaVencimiento: null,
    exoneracionPorcentaje: null,
    exoneracionCabys: null,
    exoneracionActiva: false
  });

  useEffect(() => {
    if (isEditing) {
      cargarCliente();
    }
  }, [id]);

  const cargarCliente = async () => {
    try {
      const response = await receptoresApi.getById(id);
      const data = response.data?.data;
      if (data) {
        setCliente({
          tipoIdentificacion: data.tipoIdentificacion || data.tipo_identificacion || '02',
          identificacion: data.identificacion || '',
          nombre: data.nombre || '',
          nombreComercial: data.nombreComercial || data.nombre_comercial || '',
          provincia: data.provincia || '',
          canton: data.canton || '',
          distrito: data.distrito || '',
          otrasSenas: data.otrasSenas || data.otras_senas || '',
          telefonoCodigo: data.telefonoCodigo || data.telefono_codigo || '506',
          telefonoNumero: data.telefonoNumero || data.telefono_numero || '',
          email: data.email || '',
          emailFacturacion: data.emailFacturacion || data.email_facturacion || '',
          actividadEconomica: data.actividadEconomica || data.actividad_economica || '',
          actividadEconomicaDescripcion: data.actividadEconomicaDescripcion || data.actividad_economica_descripcion || '',
          // Configuración de facturación
          generaFacturaElectronica: (data.generaFacturaElectronica ?? data.genera_factura_electronica) !== false,
          monedaDefecto: data.monedaDefecto || data.moneda_defecto || 'CRC',
          condicionVentaDefecto: data.condicionVentaDefecto || data.condicion_venta_defecto || '01',
          diasCredito: data.diasCredito || data.dias_credito || 0,
          // Configuración de crédito
          tieneCuentaCredito: !!(data.tieneCuentaCredito || data.tiene_cuenta_credito),
          limiteCredito: data.limiteCredito || data.limite_credito || null,
          creditoSuspendido: !!(data.creditoSuspendido || data.credito_suspendido),
          notasCredito: data.notasCredito || data.notas_credito || '',
          // Campos de exoneración
          exoneracionNumero: data.exoneracionNumero || data.exoneracion_numero || '',
          exoneracionTipoDoc: data.exoneracionTipoDoc || data.exoneracion_tipo_doc || '',
          exoneracionTipoDocDesc: data.exoneracionTipoDocDesc || data.exoneracion_tipo_doc_desc || '',
          exoneracionInstitucionCodigo: data.exoneracionInstitucionCodigo || data.exoneracion_institucion_codigo || '',
          exoneracionInstitucionNombre: data.exoneracionInstitucionNombre || data.exoneracion_institucion_nombre || '',
          exoneracionFechaEmision: data.exoneracionFechaEmision || data.exoneracion_fecha_emision || null,
          exoneracionFechaVencimiento: data.exoneracionFechaVencimiento || data.exoneracion_fecha_vencimiento || null,
          exoneracionPorcentaje: data.exoneracionPorcentaje || data.exoneracion_porcentaje || null,
          exoneracionCabys: (() => {
            const cabys = data.exoneracionCabys || data.exoneracion_cabys;
            if (!cabys) return null;
            if (Array.isArray(cabys)) return cabys;
            try { return JSON.parse(cabys); } catch { return null; }
          })(),
          exoneracionActiva: !!(data.exoneracionActiva || data.exoneracion_activa)
        });
        // Si tiene exoneración, configurar estados relacionados
        if (data.exoneracionNumero || data.exoneracion_numero) {
          setNumeroExoneracionInput(data.exoneracionNumero || data.exoneracion_numero);
        }
        // Establecer el tipo seleccionado si existe
        const tipoDoc = data.exoneracionTipoDoc || data.exoneracion_tipo_doc;
        if (tipoDoc) {
          setTipoExoneracionSeleccionado(tipoDoc);
          // Si el número empieza con AL-, es modo Hacienda, sino manual
          const numero = data.exoneracionNumero || data.exoneracion_numero || '';
          setModoExoneracion(numero.startsWith('AL-') ? 'hacienda' : 'manual');
        }
        // No marcamos como consultado para que pueda re-consultar Hacienda

        // Load multiple exoneraciones
        try {
          const exoRes = await receptoresApi.getExoneraciones(id);
          setExoneraciones(exoRes.data?.data || []);
        } catch (e) { /* no exoneraciones */ }
      }
    } catch (error) {
      console.error('Error loading client:', error);
      toast.error('Error al cargar cliente');
      navigate('/clientes');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field, value) => {
    setCliente(prev => ({ ...prev, [field]: value }));
    // Clear error when field changes
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    // Reset Hacienda data when identification changes
    if (field === 'identificacion') {
      setHaciendaConsultado(false);
      setActividadesHacienda([]);
    }
  };

  const handleIdentificacionChange = (e) => {
    const rawValue = e.target.value;
    const digits = getDigits(rawValue);
    const tipoConfig = TIPOS_IDENTIFICACION.find(t => t.value === cliente.tipoIdentificacion);
    const maxLength = tipoConfig?.maxLength || 20;

    // Limit to max length
    const limitedDigits = digits.slice(0, maxLength);

    // Format the value
    const formattedValue = formatIdentificacion(limitedDigits, cliente.tipoIdentificacion);

    setCliente(prev => ({ ...prev, identificacion: formattedValue }));

    // Clear errors
    if (errors.identificacion) {
      setErrors(prev => ({ ...prev, identificacion: null }));
    }

    // Reset Hacienda data and mark as user-changed
    setHaciendaConsultado(false);
    setActividadesHacienda([]);
    setUserChangedIdentificacion(true);
  };

  // Auto-query Hacienda when identification is complete
  useEffect(() => {
    const digits = getDigits(cliente.identificacion);
    const tipoConfig = TIPOS_IDENTIFICACION.find(t => t.value === cliente.tipoIdentificacion);
    const requiredLength = tipoConfig?.maxLength || 0;

    // Only auto-query for cédula física or jurídica
    const canQuery = cliente.tipoIdentificacion === '01' || cliente.tipoIdentificacion === '02';

    // For editing: only query if user actually changed the identification
    // For new clients: always allow auto-query
    const shouldQuery = !isEditing || userChangedIdentificacion;

    console.log('useEffect check:', { digits: digits.length, required: requiredLength, haciendaConsultado, consultandoHacienda, canQuery, shouldQuery, isEditing, userChangedIdentificacion });

    if (canQuery && shouldQuery && digits.length === requiredLength && !haciendaConsultado && !consultandoHacienda) {
      // Delay slightly to avoid rapid queries while typing
      const timer = setTimeout(() => {
        console.log('Calling consultarHaciendaAuto with:', digits);
        consultarHaciendaAuto(digits);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [cliente.identificacion, cliente.tipoIdentificacion, haciendaConsultado, consultandoHacienda, isEditing, userChangedIdentificacion]);

  const consultarHaciendaAuto = async (identificacion) => {
    setConsultandoHacienda(true);
    try {
      const response = await receptoresApi.consultarHacienda(identificacion);

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;

        // Auto-fill name
        if (data.nombre) {
          setCliente(prev => ({ ...prev, nombre: data.nombre }));
          toast.success(`Cliente encontrado: ${data.nombre}`);
        }

        // Set activities
        if (data.actividades && data.actividades.length > 0) {
          setActividadesHacienda(data.actividades);

          // If only one activity, auto-select it
          if (data.actividades.length === 1) {
            const actividad = data.actividades[0];
            setCliente(prev => ({
              ...prev,
              actividadEconomica: actividad.codigo,
              actividadEconomicaDescripcion: actividad.descripcion
            }));
          }
        }

        setHaciendaConsultado(true);
      }
    } catch (error) {
      console.error('Error auto-consultando Hacienda:', error);
      // Aviso solo si Hacienda está bloqueando (503/haciendaDown) — así el
      // usuario entiende por qué el nombre quedó vacío y no piensa que es bug.
      if (error.response?.status === 503 || error.response?.data?.haciendaDown) {
        toast.error('Hacienda no responde. Complete los datos manualmente.', { duration: 5000 });
        setHaciendaConsultado(true); // no reintentar
      }
    } finally {
      setConsultandoHacienda(false);
    }
  };

  const consultarHacienda = async () => {
    if (!cliente.identificacion || cliente.identificacion.length < 9) {
      toast.error('Ingrese una identificación válida');
      return;
    }

    setConsultandoHacienda(true);
    try {
      const response = await receptoresApi.consultarHacienda(cliente.identificacion);

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;

        // Auto-fill name if empty
        if (!cliente.nombre && data.nombre) {
          setCliente(prev => ({ ...prev, nombre: data.nombre }));
        }

        // Set activities
        if (data.actividades && data.actividades.length > 0) {
          setActividadesHacienda(data.actividades);

          // If only one activity, auto-select it
          if (data.actividades.length === 1) {
            const actividad = data.actividades[0];
            setCliente(prev => ({
              ...prev,
              actividadEconomica: actividad.codigo,
              actividadEconomicaDescripcion: actividad.descripcion
            }));
          }

          toast.success(`Se encontraron ${data.actividades.length} actividad(es) económica(s)`);
        } else {
          toast('No se encontraron actividades económicas', { icon: 'ℹ️' });
        }

        setHaciendaConsultado(true);
      } else {
        toast.error(response.data?.error || 'No se encontró información');
      }
    } catch (error) {
      console.error('Error consultando Hacienda:', error);
      const msg = error.response?.status === 503 || error.response?.data?.haciendaDown
        ? 'Hacienda no responde. Complete los datos manualmente y guarde.'
        : (error.response?.data?.error || 'Error al consultar Hacienda');
      toast.error(msg, { duration: 5000 });
    } finally {
      setConsultandoHacienda(false);
    }
  };

  const seleccionarActividad = (actividad) => {
    setCliente(prev => ({
      ...prev,
      actividadEconomica: actividad.codigo,
      actividadEconomicaDescripcion: actividad.descripcion
    }));
    toast.success(`Actividad seleccionada: ${actividad.codigo}`);
  };

  // Handler para cuando se selecciona una exoneración desde el modal
  const handleExoneracionSelected = async (exoneracionData) => {
    // If editing existing client, save to receptor_exoneraciones table
    if (isEditing && id) {
      try {
        const payload = {
          numeroDocumento: exoneracionData.numeroDocumento,
          tipoDocumento: exoneracionData.tipoDocumento,
          tipoDocumentoDesc: exoneracionData.tipoDocumentoDesc,
          // nombreInstitucion en el modal contiene el CÓDIGO (ej '16'); nombreInstitucionDesc el texto.
          // Persistir ambos: el código va al XML <NombreInstitucion>, el texto a UI.
          institucionCodigo: exoneracionData.nombreInstitucion,
          institucionNombre: exoneracionData.nombreInstitucionDesc || exoneracionData.nombreInstitucion,
          articuloLey: exoneracionData.articuloLey || null,
          incisoLey: exoneracionData.incisoLey || null,
          fechaEmision: exoneracionData.fechaEmision,
          fechaVencimiento: exoneracionData.fechaVencimiento || null,
          porcentaje: exoneracionData.porcentaje,
          cabys: exoneracionData.cabys || []
        };

        if (editingExoneracion) {
          await receptoresApi.updateExoneracion(id, editingExoneracion.id, payload);
          toast.success('Exoneración actualizada');
        } else {
          await receptoresApi.createExoneracion(id, payload);
          toast.success('Exoneración agregada');
        }

        // Reload exoneraciones
        const exoRes = await receptoresApi.getExoneraciones(id);
        setExoneraciones(exoRes.data?.data || []);
        setEditingExoneracion(null);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error guardando exoneración');
      }
    } else {
      // New client - save to cliente state (will be saved when client is created)
      setCliente(prev => ({
        ...prev,
        exoneracionNumero: exoneracionData.numeroDocumento,
        exoneracionTipoDoc: exoneracionData.tipoDocumento,
        exoneracionTipoDocDesc: exoneracionData.tipoDocumentoDesc,
        exoneracionInstitucionCodigo: exoneracionData.nombreInstitucion,
        exoneracionInstitucionNombre: exoneracionData.nombreInstitucionDesc,
        exoneracionFechaEmision: exoneracionData.fechaEmision,
        exoneracionFechaVencimiento: exoneracionData.fechaVencimiento || null,
        exoneracionPorcentaje: exoneracionData.porcentaje,
        exoneracionCabys: exoneracionData.cabys || [],
        exoneracionActiva: true
      }));
    }
  };

  // Quitar exoneración
  const quitarExoneracion = () => {
    setCliente(prev => ({
      ...prev,
      exoneracionNumero: '',
      exoneracionTipoDoc: '',
      exoneracionTipoDocDesc: '',
      exoneracionInstitucionCodigo: '',
      exoneracionInstitucionNombre: '',
      exoneracionFechaEmision: null,
      exoneracionFechaVencimiento: null,
      exoneracionPorcentaje: null,
      exoneracionCabys: null,
      exoneracionActiva: false
    }));
    toast.success('Exoneración eliminada');
  };

  // Formatear fecha para mostrar
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    try {
      return new Date(fecha).toLocaleDateString('es-CR');
    } catch {
      return fecha;
    }
  };

  // Verificar si la exoneración está vencida
  const exoneracionVencida = () => {
    if (!cliente.exoneracionFechaVencimiento) return false;
    return new Date(cliente.exoneracionFechaVencimiento) < new Date();
  };

  const validateForm = () => {
    const newErrors = {};
    const digits = getDigits(cliente.identificacion);

    if (!cliente.tipoIdentificacion) {
      newErrors.tipoIdentificacion = 'Seleccione el tipo de identificación';
    }

    if (!digits) {
      newErrors.identificacion = 'La identificación es requerida';
    } else {
      const tipoConfig = TIPOS_IDENTIFICACION.find(t => t.value === cliente.tipoIdentificacion);
      if (tipoConfig && digits.length !== tipoConfig.maxLength) {
        newErrors.identificacion = `Debe tener ${tipoConfig.maxLength} dígitos`;
      }
    }

    if (!cliente.nombre) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (cliente.nombre.length > 100) {
      newErrors.nombre = 'Máximo 100 caracteres';
    }

    if (cliente.nombreComercial && cliente.nombreComercial.length > 80) {
      newErrors.nombreComercial = 'Máximo 80 caracteres';
    }

    // Correo de contacto: admite múltiples separados por coma o punto y coma.
    // Se valida cada uno; en el XML de Hacienda se usa solo el primero.
    if (cliente.email) {
      const partes = cliente.email.split(/[,;]/).map(e => e.trim()).filter(Boolean);
      const invalido = partes.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      if (partes.length === 0 || invalido) {
        newErrors.email = invalido ? `Email inválido: ${invalido}` : 'Email inválido';
      }
    }

    if (cliente.otrasSenas && cliente.otrasSenas.length > 250) {
      newErrors.otrasSenas = 'Máximo 250 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor corrija los errores');
      return;
    }

    setLoading(true);
    try {
      const data = {
        tipoIdentificacion: cliente.tipoIdentificacion,
        identificacion: getDigits(cliente.identificacion), // Send only digits
        nombre: cliente.nombre,
        nombreComercial: cliente.nombreComercial || null,
        provincia: cliente.provincia || null,
        canton: cliente.canton || null,
        distrito: cliente.distrito || null,
        otrasSenas: cliente.otrasSenas || null,
        telefonoCodigo: cliente.telefonoCodigo || '506',
        telefono: cliente.telefonoNumero || null,
        email: cliente.email || null,
        emailFacturacion: cliente.emailFacturacion || null,
        actividadEconomica: cliente.actividadEconomica || null,
        actividadEconomicaDescripcion: cliente.actividadEconomicaDescripcion || null,
        // Configuración de facturación
        generaFacturaElectronica: cliente.generaFacturaElectronica,
        monedaDefecto: cliente.monedaDefecto,
        condicionVentaDefecto: cliente.condicionVentaDefecto,
        diasCredito: parseInt(cliente.diasCredito) || 0,
        // Configuración de crédito
        tieneCuentaCredito: cliente.tieneCuentaCredito || false,
        limiteCredito: cliente.limiteCredito || null,
        creditoSuspendido: cliente.creditoSuspendido || false,
        notasCredito: cliente.notasCredito || null,
        // Campos de exoneración
        exoneracionNumero: cliente.exoneracionNumero || null,
        exoneracionTipoDoc: cliente.exoneracionTipoDoc || null,
        exoneracionTipoDocDesc: cliente.exoneracionTipoDocDesc || null,
        exoneracionInstitucionCodigo: cliente.exoneracionInstitucionCodigo || null,
        exoneracionInstitucionNombre: cliente.exoneracionInstitucionNombre || null,
        exoneracionFechaEmision: cliente.exoneracionFechaEmision || null,
        exoneracionFechaVencimiento: cliente.exoneracionFechaVencimiento || null,
        exoneracionPorcentaje: cliente.exoneracionPorcentaje || null,
        exoneracionCabys: cliente.exoneracionCabys || null,
        exoneracionActiva: cliente.exoneracionActiva || false
      };

      if (isEditing) {
        await receptoresApi.update(id, data);
        toast.success('Cliente actualizado correctamente');
        // Recargar datos para confirmar que se guardó
        await cargarCliente();
        // NO navegar, quedarse en el formulario
      } else {
        const response = await receptoresApi.create(data);
        toast.success('Cliente creado');

        if (isModal && onSave) {
          // En modo modal, devolver el cliente creado
          onSave(response.data?.data);
        } else {
          navigate('/clientes');
        }
      }
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(error.response?.data?.error || 'Error al guardar cliente');
    } finally {
      setLoading(false);
    }
  };

  const cantones = cliente.provincia ? CANTONES[cliente.provincia] || [] : [];
  const distritos = cliente.provincia && cliente.canton ? getDistritosOptions(cliente.provincia, cliente.canton) : [];

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header - solo mostrar si no es modal */}
      {!isModal && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/clientes')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/clientes')}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </button>
            <button
              type="submit"
              form="cliente-form"
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isEditing ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      <form id="cliente-form" onSubmit={handleSubmit}>
        <div className={`bg-white ${isModal ? '' : 'rounded-lg border border-gray-200'} p-6`}>
          {/* Row 1: Identificación */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo ID <span className="text-red-500">*</span>
              </label>
              <select
                value={cliente.tipoIdentificacion}
                onChange={(e) => {
                  handleChange('tipoIdentificacion', e.target.value);
                  handleChange('identificacion', '');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {TIPOS_IDENTIFICACION.map(tipo => (
                  <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Identificación <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={cliente.identificacion}
                  onChange={handleIdentificacionChange}
                  placeholder={TIPOS_IDENTIFICACION.find(t => t.value === cliente.tipoIdentificacion)?.placeholder}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 pr-10 font-mono ${errors.identificacion ? 'border-red-500' : 'border-gray-300'}`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {consultandoHacienda && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                  {haciendaConsultado && !consultandoHacienda && cliente.nombre && <CheckCircle className="h-4 w-4 text-green-500" />}
                </div>
              </div>
              {errors.identificacion && <p className="mt-1 text-xs text-red-500">{errors.identificacion}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {cliente.tipoIdentificacion === '02' ? 'Razón Social' : 'Nombre'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={cliente.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                placeholder={cliente.tipoIdentificacion === '02' ? 'Empresa S.A.' : 'Juan Pérez'}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.nombre ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.nombre && <p className="mt-1 text-xs text-red-500">{errors.nombre}</p>}
            </div>
          </div>

          {/* Row 2: Nombre Comercial, Teléfono, Email */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre Comercial
              </label>
              <input
                type="text"
                value={cliente.nombreComercial}
                onChange={(e) => handleChange('nombreComercial', e.target.value)}
                placeholder="Nombre comercial (opcional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="inline h-4 w-4 mr-1" />
                Teléfono
              </label>
              <div className="flex gap-1">
                <select
                  value={cliente.telefonoCodigo}
                  onChange={(e) => handleChange('telefonoCodigo', e.target.value)}
                  className="w-20 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="506">+506</option>
                  <option value="1">+1</option>
                  <option value="507">+507</option>
                </select>
                <input
                  type="text"
                  value={cliente.telefonoNumero}
                  onChange={(e) => handleChange('telefonoNumero', e.target.value.replace(/\D/g, ''))}
                  placeholder="88888888"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="inline h-4 w-4 mr-1" />
                Correo Electrónico
              </label>
              <input
                type="text"
                value={cliente.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="contacto@empresa.com, cobros@empresa.com"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.email
                ? <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                : <p className="mt-1 text-xs text-gray-400">Puede poner varios separados por coma (,) o punto y coma (;). En la factura (XML) se usa solo el primero.</p>}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Row 3: Ubicación */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="inline h-4 w-4 mr-1" />
                Provincia
              </label>
              <select
                value={cliente.provincia}
                onChange={(e) => {
                  handleChange('provincia', e.target.value);
                  handleChange('canton', '');
                  handleChange('distrito', '');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccionar...</option>
                {PROVINCIAS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cantón</label>
              <select
                value={cliente.canton}
                onChange={(e) => {
                  handleChange('canton', e.target.value);
                  handleChange('distrito', '');
                }}
                disabled={!cliente.provincia}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">Seleccionar...</option>
                {cantones.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Distrito</label>
              <select
                value={cliente.distrito}
                onChange={(e) => handleChange('distrito', e.target.value)}
                disabled={!cliente.canton || distritos.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">Seleccionar...</option>
                {distritos.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Otras Señas / Dirección
              </label>
              <input
                type="text"
                value={cliente.otrasSenas}
                onChange={(e) => handleChange('otrasSenas', e.target.value)}
                placeholder="Dirección exacta..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Facturación Electrónica */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Facturación Electrónica
            </h3>

            {/* Checkbox Requiere FE */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cliente.generaFacturaElectronica}
                onChange={(e) => handleChange('generaFacturaElectronica', e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Requiere Factura Electrónica
                </span>
                <p className="text-xs text-gray-600 mt-0.5">
                  {cliente.generaFacturaElectronica
                    ? 'Se generará Factura Electrónica (requiere datos del receptor)'
                    : 'Se generará Tiquete Electrónico (para consumidor final)'}
                </p>
              </div>
            </label>

            {cliente.generaFacturaElectronica && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Para recibir facturas electrónicas, el cliente debe estar en estado "Inscrito" en el Ministerio de Hacienda.
                </p>
              </div>
            )}

            {/* Actividad Económica dentro de la sección de FE */}
            {cliente.generaFacturaElectronica && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Actividad Económica
                </label>
                {actividadesHacienda.length > 0 ? (
                  <>
                    <select
                      value={cliente.actividadEconomica}
                      onChange={(e) => {
                        const act = actividadesHacienda.find(a => a.codigo === e.target.value);
                        if (act) seleccionarActividad(act);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">Seleccionar actividad...</option>
                      {actividadesHacienda.map((act, idx) => (
                        <option key={idx} value={act.codigo}>
                          {act.codigo} - {act.descripcion}
                        </option>
                      ))}
                    </select>
                    {actividadesHacienda.length > 1 && (
                      <p className="text-xs text-amber-600 mt-1">
                        {actividadesHacienda.length} actividades disponibles en Hacienda
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={cliente.actividadEconomica ? `${cliente.actividadEconomica} - ${cliente.actividadEconomicaDescripcion || ''}` : ''}
                      placeholder="Sin actividad cargada"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      disabled
                    />
                    <button
                      type="button"
                      onClick={consultarHacienda}
                      disabled={consultandoHacienda || !cliente.identificacion}
                      className="px-4 py-2 border-2 border-slate-500 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    >
                      {consultandoHacienda ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Consultando...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Consultar
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Enviar Facturas por Email */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Enviar Facturas por Email
            </h3>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Email(s) para Envío de Facturas (y documentos electrónicos):
              </label>
              <input
                type="text"
                value={cliente.emailFacturacion}
                onChange={(e) => handleChange('emailFacturacion', e.target.value)}
                placeholder="facturacion@empresa.com, copia@empresa.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">Separe múltiples emails con coma (,) o punto y coma (;)</p>
            </div>
          </div>

          {/* Configuración de Venta */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Configuración de Venta
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Moneda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Moneda por Defecto
                </label>
                <select
                  value={cliente.monedaDefecto}
                  onChange={(e) => handleChange('monedaDefecto', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="CRC">₡ Colones (CRC)</option>
                  <option value="USD">$ Dólares (USD)</option>
                </select>
              </div>

              {/* Condición de venta */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condición de Venta
                </label>
                <select
                  value={cliente.condicionVentaDefecto}
                  onChange={(e) => handleChange('condicionVentaDefecto', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="01">Contado</option>
                  <option value="02">Crédito</option>
                </select>
              </div>

              {/* Días de crédito */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plazo de Pago
                </label>
                <select
                  value={cliente.diasCredito}
                  onChange={(e) => handleChange('diasCredito', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="0">Contado</option>
                  <option value="8">8 días</option>
                  <option value="15">15 días</option>
                  <option value="30">30 días</option>
                  <option value="60">60 días</option>
                </select>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Configuración de Crédito */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Configuración de Crédito
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Tiene cuenta de crédito */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="tieneCuentaCredito"
                  checked={cliente.tieneCuentaCredito || false}
                  onChange={(e) => handleChange('tieneCuentaCredito', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="tieneCuentaCredito" className="text-sm text-gray-700">
                  Cliente tiene cuenta de crédito
                </label>
              </div>

              {/* Crédito suspendido */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="creditoSuspendido"
                  checked={cliente.creditoSuspendido || false}
                  onChange={(e) => handleChange('creditoSuspendido', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <label htmlFor="creditoSuspendido" className="text-sm text-gray-700">
                  Crédito suspendido
                </label>
              </div>

              {/* Límite de crédito */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Límite de Crédito
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {cliente.monedaDefecto === 'USD' ? '$' : '¢'}
                  </span>
                  <input
                    type="number"
                    value={cliente.limiteCredito || ''}
                    onChange={(e) => handleChange('limiteCredito', e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="Sin límite (ilimitado)"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Dejar vacío para crédito ilimitado
                </p>
              </div>

              {/* Notas de crédito */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Notas de Crédito
                </label>
                <textarea
                  value={cliente.notasCredito || ''}
                  onChange={(e) => handleChange('notasCredito', e.target.value)}
                  placeholder="Notas internas sobre el crédito del cliente..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Exoneraciones de Impuestos - Múltiples */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Exoneraciones de Impuestos
                {exoneraciones.length > 0 && (
                  <span className="bg-purple-200 text-purple-800 text-xs px-2 py-0.5 rounded-full">{exoneraciones.length}</span>
                )}
              </h3>
              {isEditing && (
                <button type="button" onClick={() => { setEditingExoneracion(null); setShowExoneracionModal(true); }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-100">
                  <Plus className="h-3 w-3" /> Agregar
                </button>
              )}
            </div>

            {exoneraciones.length === 0 && !cliente.exoneracionActiva ? (
              // Sin exoneración - mostrar botón para agregar
              <div className="text-center py-4">
                <p className="text-sm text-gray-600 mb-3">
                  Este cliente no tiene exoneración configurada
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (!cliente.identificacion) {
                      toast.error('Primero ingrese la identificación del cliente');
                      return;
                    }
                    setShowExoneracionModal(true);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 mx-auto"
                >
                  <Shield className="h-4 w-4" />
                  Agregar Exoneración
                </button>
              </div>
            ) : exoneraciones.length > 0 ? (
              // Múltiples exoneraciones - tabla
              <div className="space-y-2">
                {exoneraciones.map(exo => {
                  const vencida = exo.fechaVencimiento && new Date(exo.fechaVencimiento) < new Date();
                  return (
                    <div key={exo.id} className={`border rounded-lg p-3 ${vencida ? 'bg-amber-50 border-amber-200' : exo.isActive ? 'bg-white border-purple-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-semibold text-purple-700">{exo.numeroDocumento}</span>
                            {vencida && <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">Vencida</span>}
                            {!exo.isActive && <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Inactiva</span>}
                            {exo.isActive && !vencida && <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded">Activa</span>}
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">{exo.porcentaje}%</span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-0.5">
                            {exo.tipoDocumentoDesc && <div>{exo.tipoDocumentoDesc}</div>}
                            {exo.institucionNombre && <div>{exo.institucionNombre}</div>}
                            <div>
                              {exo.fechaEmision && <span>Emisión: {new Date(exo.fechaEmision).toLocaleDateString('es-CR')}</span>}
                              {exo.fechaVencimiento && <span className="ml-3">Vence: {new Date(exo.fechaVencimiento).toLocaleDateString('es-CR')}</span>}
                            </div>
                            {exo.cabys && Array.isArray(exo.cabys) && exo.cabys.length > 0 && (() => {
                              const [expanded, setExpanded] = [exo._expanded, (v) => { exo._expanded = v; setExoneraciones([...exoneraciones]); }];
                              const shown = expanded ? exo.cabys : exo.cabys.slice(0, 5);
                              return (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  <span className="text-gray-500">CABYS:</span>
                                  {shown.map((c, i) => (
                                    <span key={i} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-mono">{c}</span>
                                  ))}
                                  {exo.cabys.length > 5 && (
                                    <button type="button" onClick={() => setExpanded(!expanded)}
                                      className="text-xs text-blue-600 hover:text-blue-800 font-medium cursor-pointer">
                                      {expanded ? 'Ver menos' : `+${exo.cabys.length - 5} más`}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button type="button" onClick={() => { setEditingExoneracion(exo); setShowExoneracionModal(true); }}
                            className="p-1 text-gray-400 hover:text-blue-600"><Pencil size={14} /></button>
                          <button type="button" onClick={async () => {
                            if (!confirm('¿Eliminar esta exoneración?')) return;
                            try {
                              await receptoresApi.deleteExoneracion(id, exo.id);
                              setExoneraciones(prev => prev.filter(e => e.id !== exo.id));
                              toast.success('Exoneración eliminada');
                            } catch (e) { toast.error('Error eliminando'); }
                          }} className="p-1 text-gray-400 hover:text-red-600"><Delete size={14} /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Legacy single exoneración - mostrar datos antiguos
              <div>
                {exoneracionVencida() ? (
                  <div className="mb-3 p-3 bg-amber-100 border border-amber-300 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Exoneración Vencida</p>
                      <p className="text-xs text-amber-700">
                        Esta exoneración venció el {formatearFecha(cliente.exoneracionFechaVencimiento)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 p-3 bg-green-100 border border-green-300 rounded-lg flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Exoneración Válida y Vigente</p>
                      <p className="text-xs text-green-700">
                        Vence el {formatearFecha(cliente.exoneracionFechaVencimiento)}
                      </p>
                    </div>
                  </div>
                )}

                <div className="bg-white border border-purple-200 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Número:</span>
                      <span className="ml-2 font-mono font-medium">{cliente.exoneracionNumero}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Porcentaje:</span>
                      <span className="ml-2 font-semibold text-purple-700">{cliente.exoneracionPorcentaje}%</span>
                    </div>
                  </div>

                  <div className="text-sm">
                    <span className="text-gray-500">Tipo:</span>
                    <span className="ml-2">{cliente.exoneracionTipoDocDesc || cliente.exoneracionTipoDoc}</span>
                  </div>

                  <div className="text-sm">
                    <span className="text-gray-500">Institución:</span>
                    <span className="ml-2">{cliente.exoneracionInstitucionNombre}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Emisión:</span>
                      <span className="ml-2">{formatearFecha(cliente.exoneracionFechaEmision)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Vencimiento:</span>
                      <span className="ml-2">{formatearFecha(cliente.exoneracionFechaVencimiento)}</span>
                    </div>
                  </div>

                  {cliente.exoneracionCabys && cliente.exoneracionCabys.length > 0 && (
                    <div className="text-sm">
                      <button
                        type="button"
                        onClick={() => setMostrarCabys(!mostrarCabys)}
                        className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors"
                      >
                        {mostrarCabys ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        <span className="text-gray-500">CABYS autorizados:</span>
                        <span className="font-semibold">{cliente.exoneracionCabys.length} códigos</span>
                      </button>
                      {mostrarCabys && (
                        <div className="mt-2 flex flex-wrap gap-1 max-h-40 overflow-y-auto p-2 bg-purple-50 rounded-lg">
                          {cliente.exoneracionCabys.map((cabys, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-mono">
                              {cabys}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={quitarExoneracion}
                  className="mt-3 flex items-center gap-2 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  <Delete className="h-5 w-5 text-red-500" />
                  <span className="text-red-500">Quitar Exoneración</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Botones para modo modal */}
        {isModal && (
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Guardar Cliente
            </button>
          </div>
        )}
      </form>

      {/* Modal de Exoneración */}
      <ExoneracionModal
        isOpen={showExoneracionModal}
        onClose={() => { setShowExoneracionModal(false); setEditingExoneracion(null); }}
        onExoneracionSelected={handleExoneracionSelected}
        clienteIdentificacion={cliente.identificacion}
        clienteNombre={cliente.nombre}
        titulo={editingExoneracion ? 'Editar Exoneración del Cliente' : 'Agregar Exoneración al Cliente'}
        subtitulo={editingExoneracion ? 'Modificá los datos de la exoneración' : 'Se guardará en el registro del cliente'}
        initialData={editingExoneracion}
      />
    </div>
  );
}
